/**
 * A tabela editável: linhas <b>e colunas</b> pela mão de quem escreve.
 *
 * <p><b>O que isto acrescenta ao {@link ./linhasEditaveis}.</b> Lá as colunas são
 * cravadas em código pela tela ({@code ETAPAS}, {@code ACOES}) e o time só mexe nas
 * linhas. Aqui a coluna também é dado: "Cenas, Ações e Fluxo" resume duas fontes que
 * ainda estão sendo reconciliadas, e a coluna que vai faltar é justamente a que ninguém
 * previu — "custo de energia" não existe no épico-01 e é canônica no GDD.
 *
 * <p><b>A álgebra de linha não é reescrita aqui</b>, é importada: reordenar é onde erro
 * de índice se esconde, e duas cópias dessa função seriam duas chances de uma delas
 * ficar para trás. O que nasce neste arquivo é a álgebra de <i>coluna</i>, que a outra
 * tela não tem — e o cruzamento das duas, que é apagar uma coluna sem deixar o valor
 * dela órfão em cada linha.
 */
import { acrescentar, escrever, mover, remover } from './linhasEditaveis';
import type { Linha } from './linhasEditaveis';

export type { Linha } from './linhasEditaveis';

export interface ColunaEditavel {
  id: string;
  rotulo: string;
  /** Coluna larga ocupa mais espaço — para o campo que recebe uma frase, não um dado. */
  larga?: boolean;
}

/**
 * Um quadro da página: o texto que apresenta o assunto, e a tabela.
 *
 * <p><b>O texto mora aqui dentro, e não num quadro à parte</b>, porque ele responde pelo
 * mesmo assunto que a tabela. Junto, ele viaja na mesma gravação, ganha a mesma versão e
 * obedece ao mesmo botão Salvar — separado, a página teria vinte e seis botões e o time
 * descobriria, ao gravar um, que o outro ficou para trás.
 *
 * <p>{@code texto} e {@code alerta} são <b>texto puro</b>. Nada de markdown: a regra da
 * casa é que o texto do time sai como foi digitado, e renderizar marcação mudaria o que
 * está escrito — um `*` de lista viraria itálico, um `#` viraria título. Linha em branco
 * separa parágrafo, que é espaçamento e não marcação.
 */
export interface Tabela {
  /**
   * O título da seção, quando o time o reescreveu.
   *
   * <p>Ausente quer dizer "vale o de fábrica" — o título que a tela dona declara em
   * {@code SecaoDeQuadros}. Ele mora no quadro, e não numa lista à parte, pela mesma
   * razão do texto: viaja na mesma gravação e obedece ao mesmo botão Salvar. O de
   * fábrica continua sendo a âncora do índice e do log quando o time não escreveu outro.
   */
  titulo?: string;
  /** A prosa que antecede a tabela. Linha em branco separa parágrafo. */
  texto?: string;
  /** O aviso em destaque, quando há um. Vazio não desenha a caixa. */
  alerta?: string;
  /**
   * A família de cada linha, pelo id dela — o que pinta o fundo da linha.
   *
   * <p><b>Fora da linha, e não dentro dela</b>, porque a família não é conteúdo: ela não
   * se lê, não se edita e não se exporta junto do texto. Guardá-la em {@link Linha} a
   * faria viajar em toda tabela do produto, inclusive nas que não têm cor nenhuma — e
   * apareceria no dia em que alguém exportasse uma tabela para outro lugar.
   *
   * <p>Por id, e não por posição: a linha é reordenável, e um índice viraria a cor errada
   * na primeira seta clicada.
   */
  familias?: Record<string, string>;
  /**
   * Em quais entregas cada linha entra, pelo id dela — os checkboxes de Alpha
   * interno, Closed Beta, Season 1 e Season 2+.
   *
   * <p>Mesmo desenho de {@link familias}, pelas mesmas razões: fora da linha porque
   * não é conteúdo que se lê ou exporta, e por id porque a linha é reordenável.
   * Quais entregas existem é vocabulário da página, não deste modelo — o componente
   * recebe a lista de fora, como recebe as cores.
   */
  entregas?: Record<string, string[]>;
  colunas: ColunaEditavel[];
  linhas: Linha[];
}

/*
 * Não há mais um tipo para "o documento inteiro", e a ausência é a mudança.
 *
 * <p>Ele existia como `{ versao, tabelas: { … } }`, de quando a página era gravada de
 * uma vez. Com a escrita por quadro, cada quadro é uma chave de primeiro nível e o
 * documento deixou de ter forma própria — é um mapa de quadros, e quem o lê é
 * `combinar`, que aceita os dois formatos. Um tipo aqui só descreveria o lado antigo.
 */

// ------------------------------------------------- montagem de conteúdo de fábrica

/**
 * Monta uma tabela de fábrica a partir de colunas e de linhas na ordem das colunas.
 *
 * <p>Era função privada de `conteudoDeCenasAcoes`, e subiu para cá quando a página
 * Personagem passou a montar as próprias tabelas: dois construtores idênticos seriam
 * duas chances de um deles gerar id de linha de outro jeito — e o id é o que casa a
 * linha com a família e com as entregas.
 */
export function montarTabela(colunas: ColunaEditavel[], celulas: string[][]): Tabela {
  const linhas: Linha[] = celulas.map((valores, i) => ({
    id: `l${i + 1}`,
    valores: Object.fromEntries(colunas.map((c, j) => [c.id, valores[j] ?? ''])),
  }));
  return { colunas, linhas };
}

export const col = (id: string, rotulo: string, larga = false): ColunaEditavel =>
  larga ? { id, rotulo, larga: true } : { id, rotulo };

/** Monta o mapa `linha → família` na ordem em que as linhas foram declaradas. */
export const familiasNaOrdem = (...nomes: string[]): Record<string, string> =>
  Object.fromEntries(nomes.map((familia, i) => [`l${i + 1}`, familia]));

// ------------------------------------------------------------------- colunas

/**
 * O próximo id de coluna.
 *
 * <p>Procura um livre em vez de contar: as colunas de fábrica têm id com significado
 * (`tipo`, `oQue`), e uma contagem sobre elas devolveria sempre `c1`. Id repetido faria
 * duas colunas escreverem na mesma chave de `valores` — o texto de uma aparecendo na
 * outra, que é o mesmo defeito que `proximoId` evita nas linhas.
 */
export function proximoIdDeColuna(colunas: ColunaEditavel[]): string {
  const usados = new Set(colunas.map((c) => c.id));
  for (let n = 1; ; n++) {
    const id = `c${n}`;
    if (!usados.has(id)) return id;
  }
}

export function acrescentarColuna(tabela: Tabela, rotulo = ''): Tabela {
  const id = proximoIdDeColuna(tabela.colunas);
  return {
    colunas: [...tabela.colunas, { id, rotulo }],
    // A coluna nasce vazia em toda linha: sem isto, `valores[id]` seria `undefined` e o
    // input viraria não-controlado no primeiro caractere digitado.
    linhas: tabela.linhas.map((l) => ({ ...l, valores: { ...l.valores, [id]: '' } })),
  };
}

/**
 * Apaga a coluna — e, com ela, o valor dela em cada linha.
 *
 * <p>Deixar os valores para trás pareceria inofensivo (nada os lê), até alguém
 * acrescentar uma coluna nova que caia no mesmo id e herdar texto de uma coluna que
 * ninguém lembra ter existido.
 *
 * <p><b>A última coluna não sai.</b> Uma tabela sem coluna nenhuma não tem onde receber
 * texto: ela viraria uma lista de linhas invisíveis, e a única saída seria apagar a
 * tabela inteira. Quem quer começar do zero renomeia a que sobrou.
 */
export function removerColuna(tabela: Tabela, id: string): Tabela {
  if (tabela.colunas.length <= 1) return tabela;
  if (!tabela.colunas.some((c) => c.id === id)) return tabela;
  return {
    colunas: tabela.colunas.filter((c) => c.id !== id),
    linhas: tabela.linhas.map((l) => ({
      ...l,
      valores: Object.fromEntries(Object.entries(l.valores).filter(([chave]) => chave !== id)),
    })),
  };
}

export function renomearColuna(tabela: Tabela, id: string, rotulo: string): Tabela {
  return {
    ...tabela,
    colunas: tabela.colunas.map((c) => (c.id === id ? { ...c, rotulo } : c)),
  };
}

/** Troca a coluna de lugar com a vizinha. Nas pontas não faz nada — como em `mover`. */
export function moverColuna(tabela: Tabela, id: string, passo: -1 | 1): Tabela {
  const i = tabela.colunas.findIndex((c) => c.id === id);
  const j = i + passo;
  if (i < 0 || j < 0 || j >= tabela.colunas.length) return tabela;
  const colunas = [...tabela.colunas];
  [colunas[i], colunas[j]] = [colunas[j], colunas[i]];
  return { ...tabela, colunas };
}

// --------------------------------------------------------------------- prosa

export const escreverTexto = (tabela: Tabela, texto: string): Tabela => ({ ...tabela, texto });

export const escreverTitulo = (tabela: Tabela, titulo: string): Tabela => ({ ...tabela, titulo });

export const escreverAlerta = (tabela: Tabela, alerta: string): Tabela => ({ ...tabela, alerta });

// -------------------------------------------------------------------- cores

/**
 * Troca a família — e, com ela, a cor de fundo — de uma linha.
 *
 * <p>Cria o mapa quando a tabela ainda não tem nenhum: uma tabela sem cor que ganha a
 * primeira é o caso normal, e exigir que ela já tivesse o mapa faria a coluna de cor
 * funcionar em algumas tabelas e em outras não, sem nada na tela explicando a diferença.
 */
export const escreverFamilia = (tabela: Tabela, idDaLinha: string, familia: string): Tabela => ({
  ...tabela,
  familias: { ...tabela.familias, [idDaLinha]: familia },
});

// ------------------------------------------------------------------ entregas

/**
 * Marca ou desmarca uma entrega numa linha — o clique do checkbox.
 *
 * <p>Cria o mapa e a lista quando ainda não existem, pela mesma razão de
 * `escreverFamilia`: toda tabela começa sem nenhuma marcação, e esse é o estado de
 * fábrica de propósito — pré-marcar seria a tela decidindo escopo pelo time.
 */
export function alternarEntrega(tabela: Tabela, idDaLinha: string, entrega: string): Tabela {
  const atuais = tabela.entregas?.[idDaLinha] ?? [];
  const novas = atuais.includes(entrega)
    ? atuais.filter((e) => e !== entrega)
    : [...atuais, entrega];
  return { ...tabela, entregas: { ...tabela.entregas, [idDaLinha]: novas } };
}

// -------------------------------------------------------------------- linhas

export const acrescentarLinha = (tabela: Tabela): Tabela => ({
  ...tabela,
  linhas: acrescentar(tabela.linhas, tabela.colunas),
});

export const removerLinha = (tabela: Tabela, id: string): Tabela => ({
  ...tabela,
  linhas: remover(tabela.linhas, id),
});

export const moverLinha = (tabela: Tabela, id: string, passo: -1 | 1): Tabela => ({
  ...tabela,
  linhas: mover(tabela.linhas, id, passo),
});

export const escreverCelula = (
  tabela: Tabela,
  linha: string,
  coluna: string,
  valor: string
): Tabela => ({ ...tabela, linhas: escrever(tabela.linhas, linha, coluna, valor) });

// ------------------------------------------------------------- o que chega de fora

/**
 * Confere a forma de uma tabela que veio do backend.
 *
 * <p>Mesma razão de `linhasEditaveis.lerLinhas`: o que está guardado é JSON que uma
 * versão anterior desta tela escreveu, e uma coluna sem `id` — ou um valor que não é
 * texto — derruba a página inteira no render. Descartar o malformado custa nada e é a
 * diferença entre uma tabela vazia e uma tela branca.
 *
 * <p>Devolve `null` quando não sobra nada aproveitável, para quem chama saber cair no
 * conteúdo de fábrica em vez de desenhar uma tabela sem coluna.
 */
export function normalizarTabela(cru: unknown): Tabela | null {
  if (!cru || typeof cru !== 'object') return null;
  const { colunas, linhas } = cru as { colunas?: unknown; linhas?: unknown };
  if (!Array.isArray(colunas)) return null;

  /*
   * Ausente e vazio são coisas diferentes, e a distinção é o que protege a prosa de
   * fábrica: `undefined` é "esta gravação nem sabia que havia texto" — e cai no padrão,
   * em `combinar`. String vazia é "o time apagou o texto", e é respeitada.
   */
  const { titulo, texto, alerta, familias, entregas } = cru as {
    titulo?: unknown;
    texto?: unknown;
    alerta?: unknown;
    familias?: unknown;
    entregas?: unknown;
  };

  const limpas: ColunaEditavel[] = [];
  const vistos = new Set<string>();
  for (const c of colunas) {
    if (!c || typeof c !== 'object') continue;
    const { id, rotulo, larga } = c as Record<string, unknown>;
    // Id repetido é pior que coluna a menos: as duas escreveriam na mesma chave.
    if (typeof id !== 'string' || !id || vistos.has(id)) continue;
    vistos.add(id);
    limpas.push({
      id,
      rotulo: typeof rotulo === 'string' ? rotulo : '',
      ...(larga === true ? { larga: true } : {}),
    });
  }
  if (limpas.length === 0) return null;

  const linhasLimpas: Linha[] = (Array.isArray(linhas) ? linhas : [])
    .filter((l): l is Record<string, unknown> => Boolean(l) && typeof l === 'object')
    .filter((l) => typeof l.id === 'string' && l.id)
    .map((l) => {
      const valores = (l.valores ?? {}) as Record<string, unknown>;
      return {
        id: l.id as string,
        valores: Object.fromEntries(
          limpas.map((c): [string, string] => {
            // Ligado a uma variável, e não lido direto de `valores[c.id]`: o
            // TypeScript não estreita `unknown` num acesso por índice de chave
            // não-literal, e o `typeof` ali não valeria de nada.
            const valor = valores[c.id];
            return [c.id, typeof valor === 'string' ? valor : ''];
          })
        ),
      };
    });

  return {
    ...(typeof titulo === 'string' ? { titulo } : {}),
    ...(typeof texto === 'string' ? { texto } : {}),
    ...(typeof alerta === 'string' ? { alerta } : {}),
    ...(familias && typeof familias === 'object'
      ? { familias: somenteTexto(familias as Record<string, unknown>) }
      : {}),
    ...(entregas && typeof entregas === 'object'
      ? { entregas: somenteListasDeTexto(entregas as Record<string, unknown>) }
      : {}),
    colunas: limpas,
    linhas: linhasLimpas,
  };
}

/** Só listas de texto sobrevivem — o resto é gravação de outra versão da tela. */
function somenteListasDeTexto(mapa: Record<string, unknown>): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(mapa)
      .filter((par): par is [string, unknown[]] => Array.isArray(par[1]))
      .map(([id, lista]): [string, string[]] => [
        id,
        lista.filter((e): e is string => typeof e === 'string'),
      ])
  );
}

/** Descarta o que não é texto — a família vira nome de classe CSS. */
function somenteTexto(mapa: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(mapa).filter((par): par is [string, string] => typeof par[1] === 'string')
  );
}

/**
 * Junta o que o time escreveu com o conteúdo de fábrica.
 *
 * <p><b>A tabela guardada vence inteira</b>, e não campo a campo. Mesclar faria uma
 * linha de fábrica apagada pelo time reaparecer na próxima abertura — e ninguém apaga
 * duas vezes a mesma linha antes de desistir da tela. Só as tabelas que <i>nunca</i>
 * foram tocadas caem no padrão, que é o que faz uma seção nova aparecer para quem já
 * tinha gravado a página antes de ela existir.
 */
export function combinar(
  padrao: Record<string, Tabela>,
  guardado: unknown
): Record<string, Tabela> {
  const doc = (guardado && typeof guardado === 'object' ? guardado : {}) as Record<
    string,
    unknown
  >;

  /*
   * Dois formatos convivem, e é de propósito.
   *
   * O documento nasceu como `{ versao, tabelas: { … } }`. Quando a gravação passou a ser
   * POR QUADRO, cada quadro virou chave de primeiro nível — foi o que permitiu o
   * servidor mesclar sem conhecer a forma do que guarda: gravar é `put(quadro, valor)`,
   * e um caminho aninhado obrigaria o backend a saber que existe um `tabelas` no meio.
   *
   * O aninhado continua sendo lido porque já há página gravada nele. Não há conversão em
   * lote: o quadro migra sozinho na primeira vez que alguém o grava, e até lá é lido de
   * onde está. Migrar tudo de uma vez exigiria uma migration que soubesse a forma do
   * conteúdo — exatamente o que a coluna `jsonb` opaca existe para evitar.
   */
  const aninhado = (doc.tabelas ?? {}) as Record<string, unknown>;

  return Object.fromEntries(
    Object.entries(padrao).map(([chave, deFabrica]) => {
      const guardada = normalizarTabela(doc[chave]) ?? normalizarTabela(aninhado[chave]);
      return [chave, guardada ? completarComFabrica(guardada, deFabrica) : deFabrica];
    })
  );
}

/**
 * Completa com o de fábrica o que a gravação não trouxe.
 *
 * <p>É a única exceção ao "o quadro guardado vence inteiro", e ela existe por uma razão
 * datada: os quadros gravados <b>antes</b> de a prosa e as cores existirem não têm esses
 * campos. Sem esta função, abrir a página depois da mudança apagaria a apresentação de
 * todas as seções que alguém já tinha salvado — um texto que ninguém escreveu e ninguém
 * mandou apagar —, e as duas tabelas coloridas voltariam ao cinza.
 *
 * <p>Só vale para <b>ausente</b>. Texto apagado de propósito é string vazia, e continua
 * vazio — ver `normalizarTabela`.
 *
 * <p>As famílias vêm em bloco, e não linha a linha: elas são a mesma taxonomia para a
 * tabela inteira, e mesclar as duas origens daria uma metade colorida pelo código e
 * outra pelo que estava gravado — o pior dos dois, e impossível de explicar olhando.
 */
function completarComFabrica(guardada: Tabela, deFabrica: Tabela): Tabela {
  return {
    ...guardada,
    titulo: guardada.titulo ?? deFabrica.titulo,
    texto: guardada.texto ?? deFabrica.texto,
    alerta: guardada.alerta ?? deFabrica.alerta,
    familias: guardada.familias ?? deFabrica.familias,
    // Nenhuma tabela de fábrica traz entregas (o escopo é decisão do time, não da
    // tela), mas a regra fica simétrica: gravação antiga não apaga o que o padrão
    // um dia passar a trazer.
    entregas: guardada.entregas ?? deFabrica.entregas,
  };
}
